import React from 'react';
import { FaShieldAlt, FaClock, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';
import Layout from '../components/Layout';
import SEO from '../components/SEO';

const TermsOfService: React.FC = () => {
  const sections = [
    {
      icon: FaShieldAlt,
      title: 'Acceptation des Conditions',
      content: 'En accédant et en utilisant ce site, vous acceptez d\'être lié par les présentes conditions d\'utilisation. Si vous n\'acceptez pas ces conditions, veuillez ne pas utiliser notre site.',
    },
    {
      icon: FaClock,
      title: 'Utilisation du Site',
      content: 'Vous vous engagez à utiliser ce site uniquement à des fins légales et conformément à ces conditions. Vous ne devez pas utiliser le site de manière à violer les lois applicables ou à porter atteinte aux droits d\'autrui.',
    },
    {
      icon: FaExclamationTriangle,
      title: 'Avertissements',
      content: 'Les informations fournies sur ce site sont à titre informatif uniquement. Nous ne garantissons pas l\'exactitude, l\'exhaustivité ou l\'actualité des informations. Les paris sportifs comportent des risques financiers.',
    },
    {
      icon: FaCheckCircle,
      title: 'Propriété Intellectuelle',
      content: 'Tous les contenus de ce site, y compris les textes, images, logos et designs, sont la propriété de Parismin ou de ses partenaires et sont protégés par les lois sur la propriété intellectuelle.',
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Conditions d\'Utilisation - Parismin',
    description: 'Conditions d\'utilisation du site Parismin. Lisez attentivement avant d\'utiliser nos services.',
  };

  return (
    <Layout>
      <SEO
        title="Conditions d'Utilisation - Parismin"
        description="Conditions d'utilisation du site Parismin. Consultez nos termes et conditions avant d'utiliser nos services."
        url="/terms"
        jsonLd={jsonLd}
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-block mb-6 px-4 py-2 glass-card rounded-full text-sm font-semibold border border-purple-500/30">
            <FaShieldAlt className="inline mr-2 text-purple-400" />
            Conditions
          </div>
          <h1 className="text-5xl md:text-6xl font-black mb-6 heading-font">
            <span className="text-gradient">Conditions d'Utilisation</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Introduction */}
            <div className="glass-card rounded-3xl p-8 md:p-12 mb-12">
              <h2 className="text-3xl font-black mb-6 heading-font text-gradient">Introduction</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Bienvenue sur Parismin. Ces conditions d'utilisation régissent votre accès et votre utilisation de notre site web. En utilisant notre site, vous acceptez d'être lié par ces conditions.
              </p>
              <p className="text-gray-300 leading-relaxed">
                Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser notre site. Nous nous réservons le droit de modifier ces conditions à tout moment, et ces modifications entreront en vigueur dès leur publication sur le site.
              </p>
            </div>

            {/* Main Sections */}
            <div className="space-y-8 mb-12">
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <div key={index} className="glass-card rounded-3xl p-8">
                    <div className="flex items-start space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 via-purple-500 to-pink-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                        <Icon className="text-white text-2xl" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-4 heading-font">{section.title}</h3>
                        <p className="text-gray-300 leading-relaxed">{section.content}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Additional Information */}
            <div className="glass-card rounded-3xl p-8 md:p-12">
              <h2 className="text-3xl font-black mb-6 heading-font text-gradient">Informations Complémentaires</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Responsabilité</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Parismin ne peut être tenu responsable des pertes ou dommages résultant de l'utilisation de ce site ou des informations qu'il contient. Les paris sportifs comportent des risques, et vous devez toujours parier de manière responsable.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Liens Externes</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Notre site peut contenir des liens vers des sites web tiers. Nous ne sommes pas responsables du contenu ou des pratiques de ces sites externes. Nous vous encourageons à lire les conditions d'utilisation et les politiques de confidentialité de ces sites.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Modifications</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Nous nous réservons le droit de modifier ces conditions d'utilisation à tout moment. Les modifications seront publiées sur cette page avec une date de mise à jour. Votre utilisation continue du site après ces modifications constitue votre acceptation des nouvelles conditions.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Droit Applicable</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Ces conditions d'utilisation sont régies par le droit français. Tout litige relatif à ces conditions sera soumis à la juridiction exclusive des tribunaux français.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold mb-3 heading-font">Contact</h3>
                  <p className="text-gray-300 leading-relaxed">
                    Pour toute question concernant ces conditions d'utilisation, veuillez nous contacter à{' '}
                    <a href="mailto:contact@parismin.com" className="text-cyan-400 hover:text-cyan-300 underline">contact@parismin.com</a>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TermsOfService;

